import { locale } from "../i18n";

/**
 * Moving between the houses a guest has stayed at (menu "Unterkunft").
 *
 * One guest account is reused at every BELTURO house its owner stays at, but
 * each house runs the guest-app on its own subdomain and localStorage is per
 * origin — the session cannot follow. Like the client-admin's house switcher,
 * the API hands out a short-lived single-use token
 * (POST /auth/guest-accommodation-switch) which the other house's TokenState
 * exchanges for a session there, so the guest stays signed in.
 */
export class AccommodationSwitch
{
    constructor( app )
    {
        this.app = app;
        // The live houses this guest has signed in at, by name. Empty for
        // "Als Gast fortfahren" and until load() answered.
        this.houses = [];
    }

    /**
     * Fetch the guest's houses. Never rejects — without the list the menu
     * simply has no "Unterkunft" item.
     */
    async load()
    {
        this.houses = [];
        if ( !this.app.user || this.app.user.guest === true )
        {
            return;
        }

        try
        {
            const { result } = await this.app.api.get( "/auth/guest-accommodation" );
            if ( result && +result.status === 200 && Array.isArray( result.data ) )
            {
                this.houses = result.data
                    .slice()
                    .sort( ( a, b ) => String( a.name ).localeCompare( String( b.name ), locale() ) );
            }
        }
        catch ( e )
        {
            console.error( e );
        }
    }

    reset()
    {
        this.houses = [];
    }

    /** Is there anywhere to switch to — this house plus at least one other? */
    canSwitch()
    {
        return this.houses.length > 1 && !!this.app.accommodation;
    }

    /**
     * Open another house's guest-app, signed in. Resolves false when the
     * switch could not be started (the page stays); on success the browser
     * leaves.
     */
    async switchTo( accommodationId )
    {
        try
        {
            const { result } = await this.app.api.post( "/auth/guest-accommodation-switch", { accommodationId } );
            if ( !result || +result.status !== 201 || !result.data?.token )
            {
                throw new Error( "Unexpected switch response." );
            }
            window.location.assign( AccommodationSwitch.guestAppUrl( result.data.slug ) +
                "#/token/" + encodeURIComponent( result.data.token ) );
            return true;
        }
        catch ( e )
        {
            console.error( e );
            return false;
        }
    }

    /**
     * This app's address for another house: the tenant is the first label of
     * the host name, in production ({slug}.belturo.app) as in development
     * ({slug}.localhost:3000) — see Theme.getTenantSlug.
     */
    static guestAppUrl( slug )
    {
        const { protocol, port, pathname, hostname } = window.location;
        const labels = hostname.split( "." );
        const baseHost = labels.length > 1 ? labels.slice( 1 ).join( "." ) : labels[ 0 ];
        return protocol + "//" + encodeURIComponent( slug ) + "." + baseHost +
            ( port ? ":" + port : "" ) + pathname;
    }
}
